/**
 * Today's "Needs you": only what waits on you, grouped by what you do with it — Decide, New for you to do, For you.
 * Each row leaves when you act (decide, give it a day or take it, reply or tick Seen). Everything else — team comments,
 * notes, other apps — is one quiet "Other updates" line at the bottom of Today, for when you're tracing something.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Check, CheckCircle2, ChevronDown, ChevronRight, ExternalLink, FileText, Gavel, Inbox, MessageSquare, Reply } from "lucide-react";
import { useState } from "react";
import { api, post } from "../lib/api";
import { MoveTask } from "./plan/TaskSteps";
import { Card, ErrorNote } from "./ui";

interface Row { askFrom?: string | null; askApp?: string | null; id: string; ref?: string; title: string; at: string; by?: string | null; personal?: boolean; spaceName?: string; n?: number; snippet?: string; team?: string; isNew?: boolean; url?: string | null; system?: string }
interface CameIn {
  since: string; asOf: string; firstLook: boolean;
  todo: { handed: Row[]; captured: Row[]; pool: Row[] };
  forYou: { comments: Row[]; finished: Row[] };
  updates: { comments: Row[]; notes: Row[]; apps: Row[] };
  sources?: { procurement: AskRow[]; mythos: AskRow[]; fusiontask: AskRow[]; ops: AskRow[]; other: AskRow[] };
  delegated?: { tasks: Array<Row & { dueAt: string | null; plannedDate: string | null; state: string }>; apps: Array<{ system: string; app: string; id: string; ref: string | null; title: string; url: string | null; status: string; dueAt: string | null; at: string }> };
  approvals?: { procurement: Array<{ id: string; ref: string; title: string; amount: string | null; currency: string | null; at: string; questionState: "awaiting_answer" | "answered" | null }> };
}
interface AskRow extends Row { source: string; app: string; type: string; urgency: string; neededBy: string | null }
export interface DecideItem { kind: "inbox" | "task"; id: string; title: string; reason: string; category: string; priority: string; link: string; dueAt: string | null }

const SOURCE: Record<string, string> = { ask: "Ask Liam", outlook: "from Outlook", event: "from another app", bus: "from another app", ask_liam: "Ask Liam", request: "request", workflow: "workflow", assistant: "Fusion AI", mail: "from Mail", whatsapp: "from WhatsApp" };
const ago = (iso: string) => {
  const m = Math.round((Date.now() - Date.parse(iso)) / 60_000);
  if (m < 60) return `${Math.max(1, m)}m ago`;
  if (m < 24 * 60) return `${Math.round(m / 60)}h ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};
const sinceLabel = (iso: string) => {
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  return today ? `since ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}` : `since ${d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}`;
};

const useCameIn = () => useQuery({ queryKey: ["came-in"], queryFn: () => api<CameIn>("/api/came-in"), refetchInterval: 120_000 });

function Section({ icon, title, count, children }: { icon: React.ReactNode; title: string; count: number; children: React.ReactNode }) {
  return (
    <div className="border-t border-[var(--line)] pb-1 pt-2.5 first:border-t-0 first:pt-0">
      <h3 className="section-label mb-1 flex items-center gap-1.5 px-5">{icon} {title} <span className="muted">· {count}</span></h3>
      {children}
    </div>
  );
}

function More<T>({ rows, n = 5, render }: { rows: T[]; n?: number; render: (r: T) => React.ReactNode }) {
  const [all, setAll] = useState(false);
  return (
    <>
      <ul className="px-2">{(all ? rows : rows.slice(0, n)).map(render)}</ul>
      {rows.length > n && <div className="px-5"><button className="btn btn-ghost btn-sm -ml-2 text-brand-700" onClick={() => setAll((x) => !x)}>{all ? "Show fewer" : `Show all ${rows.length}`}</button></div>}
    </>
  );
}

const Tag = ({ children }: { children: React.ReactNode }) => <span className="chip max-w-[16rem] shrink-0 truncate text-xs" title={typeof children === "string" ? children : undefined}>{children}</span>;
const TaskLink = ({ r, sub }: { r: Row; sub?: React.ReactNode }) => (
  <Link to="/work/$id" params={{ id: r.id }} className="min-w-0 flex-1">
    <span className="block truncate text-sm font-medium">{r.title}</span>
    {sub && <span className="muted block truncate text-xs">{sub}</span>}
  </Link>
);

/** A team-pool task: Take it makes it yours. */
function TakeIt({ id }: { id: string }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  return (
    <span className="shrink-0">
      <button type="button" className="btn btn-sm" disabled={busy} onClick={() => { setBusy(true); setErr(null); void post(`/api/work/items/${id}/claim`, {}).then(() => { for (const k of ["came-in", "my-day", "plan-today"]) void qc.invalidateQueries({ queryKey: [k] }); }, (e) => { setErr(e); setBusy(false); }); }}>Take it</button>
      {!!err && <ErrorNote error={err} />}
    </span>
  );
}

/** A comment for you (or delegated work now done): reply right here, or tick Seen. */
function ForYouRow({ r, kind }: { r: Row; kind: "comment" | "done" }) {
  const qc = useQueryClient();
  const [replying, setReplying] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const run = async (f: () => Promise<unknown>) => {
    setBusy(true); setErr(null);
    try { await f(); await qc.invalidateQueries({ queryKey: ["came-in"] }); } catch (e) { setErr(e); setBusy(false); }
  };
  const seen = () => void run(() => post("/api/came-in/ack", { itemId: r.id }));
  const send = () => text.trim() && void run(() => post(`/api/work/items/${r.id}/comments`, { body: text.trim() }));
  return (
    <li className="rounded-lg px-3 py-2 hover:bg-black/[0.03]">
      <div className="flex items-start gap-3">
        {kind === "comment" ? <MessageSquare size={15} className="mt-0.5 shrink-0 text-brand-600" /> : <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-emerald-600" />}
        <TaskLink r={r} sub={kind === "comment"
          ? <>{r.by ?? "Someone"}{r.n && r.n > 1 ? ` +${r.n - 1}` : ""}: {r.snippet}</>
          : <>Done by {r.by ?? "someone"} · check it</>} />
        <span className="muted mt-0.5 shrink-0 text-xs">{ago(r.at)}</span>
        {kind === "comment" && !replying && <button type="button" className="btn btn-sm shrink-0" onClick={() => setReplying(true)}><Reply size={13} /> Reply</button>}
        <button type="button" className="btn btn-sm shrink-0" disabled={busy} onClick={seen} title="Leaves this list; it's still on the task"><Check size={13} /> Seen</button>
      </div>
      {replying && (
        <div className="ml-7 mt-2 flex gap-2">
          <input className="input !py-1 text-sm" autoFocus value={text} maxLength={8000} placeholder={`Reply to ${r.by ?? "them"}`} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); if (e.key === "Escape") setReplying(false); }} />
          <button type="button" className="btn btn-primary btn-sm shrink-0" disabled={busy || !text.trim()} onClick={send}>Send</button>
        </div>
      )}
      {!!err && <div className="ml-7"><ErrorNote error={err} /></div>}
    </li>
  );
}

/** One box of Needs you: a header with its count, and every row (the list scrolls past ~8 rows). */
function Box({ icon, title, count, link, children }: { icon: React.ReactNode; title: string; count: number; link?: { to: string; label: string }; children: React.ReactNode }) {
  if (!count) return null;
  return (
    <Card title={title} icon={icon} count={count} flush actions={link && <a href={link.to} className="text-xs font-semibold text-brand-600 hover:underline">{link.label}</a>}>
      <ul className="max-h-[22rem] overflow-y-auto px-2 pb-2">{children}</ul>
    </Card>
  );
}

/** First of each id only: a task can be handed to you and in a pool, or carry two requests. */
const uniq = <T,>(rows: T[], id: (r: T) => string) => { const seen = new Set<string>(); return rows.filter((r) => (seen.has(id(r)) ? false : (seen.add(id(r)), true))); };
const urgent = (r: { priority?: string; urgency?: string }) => r.priority === "P1" || r.urgency === "today";
const shortDate = (d: string) => new Date(`${d.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
const eur = (n: string | null, c: string | null) => (n == null ? "" : `${c && c !== "EUR" ? `${c} ` : "€"}${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

/** A request from another app (Ask Liam): who asked, what kind, how urgent. */
function AskLine({ r }: { r: AskRow }) {
  return (
    <li className="flex items-center gap-2 rounded-lg px-3 py-2 hover:bg-black/[0.03]">
      <TaskLink r={r} sub={`${r.by ?? r.app} · ${r.type}${r.neededBy ? ` · needed by ${shortDate(r.neededBy)}` : ""}`} />
      {urgent(r) && <span className="chip shrink-0 text-xs text-red-700">Urgent</span>}
    </li>
  );
}

/**
 * "Needs you" on Today, one box per kind of work: from staff, shared with comments, Procurement approvals, Mythos
 * Tasks, FusionTask, Ops Inbox — then Other decisions for anything that fits none of them. Empty boxes are hidden.
 */
export function CameInCard({ hide, decide = [], renderDecide }: { hide?: Set<string>; decide?: DecideItem[]; renderDecide?: (items: DecideItem[]) => React.ReactNode }) {
  const q = useCameIn();
  const d = q.data;
  const keep = <T extends Row>(rows: T[] = []) => rows.filter((r) => !hide?.has(r.id));
  const todoAll = d ? [
    ...keep(d.todo.handed).map((r) => ({ r, tag: `from ${r.by}`, pool: false })),
    ...keep(d.todo.captured).map((r) => ({ r, tag: SOURCE[r.by ?? ""] ?? `from ${r.by}`, pool: false })),
    ...keep(d.todo.pool).map((r) => ({ r, tag: `${r.by} pool`, pool: true })),
  ].sort((a, b) => Date.parse(b.r.at) - Date.parse(a.r.at)) : [];
  const todo = uniq(todoAll, (t) => t.r.id);
  // finished work doesn't stay on Today (Liam, 23 Oct): only comments meant for you
  const forYou = d ? d.forYou.comments.map((r) => ({ r, kind: "comment" as const })).sort((a, b) => Date.parse(b.r.at) - Date.parse(a.r.at)) : [];
  const src = d?.sources;
  const awards = d?.approvals?.procurement ?? [];
  // the app boxes list every open request, even ones also in Do today: you asked to see them all there
  const u = (rows: AskRow[] = []) => uniq(rows, (r) => r.id);
  const procAsks = u(src?.procurement), mythos = u(src?.mythos), fusion = u(src?.fusiontask), ops = u(src?.ops), otherAsks = u(src?.other);
  // anything in a box above isn't repeated under Other decisions
  const covered = new Set<string>([...awards.map((a) => a.id), ...[procAsks, mythos, fusion, ops].flat().map((a) => a.id), ...todo.map((t) => t.r.id), ...forYou.map((f) => f.r.id)]);
  const otherDecide = decide.filter((x) => !covered.has(x.link.split("/").pop() ?? x.id) && !covered.has(x.id));
  const nOther = otherDecide.length + otherAsks.length;
  const nDelegated = (d?.delegated?.tasks.length ?? 0) + (d?.delegated?.apps.length ?? 0) + fusion.length;
  const total = todo.length + forYou.length + awards.length + procAsks.length + mythos.length + nDelegated + ops.length + nOther;
  return (
    <section aria-label="Needs you" className="space-y-3">
      <h2 className="flex items-center gap-2 text-base font-semibold"><Gavel size={16} /> Needs you {total > 0 && <span className="chip text-xs">{total}</span>}</h2>
      {!total && <p className="muted rounded-xl border border-[var(--line)] px-5 py-3 text-sm">{q.isLoading ? "Loading…" : "Nothing needs you right now."}</p>}
      <div className="grid gap-4 md:grid-cols-2">
        <Box icon={<Inbox size={16} />} title="From staff" count={todo.length}>
          {todo.map(({ r, tag, pool }) => (
            <li key={r.id} className="flex items-center gap-2 rounded-lg px-3 py-2 hover:bg-black/[0.03]">
              <TaskLink r={r} sub={tag} />
              {pool ? <TakeIt id={r.id} /> : <MoveTask id={r.id} title={r.title} plan compact />}
            </li>
          ))}
        </Box>
        <Box icon={<MessageSquare size={16} />} title="Shared with comments" count={forYou.length}>
          {forYou.map(({ r, kind }) => <ForYouRow key={`${kind}:${r.id}`} r={r} kind={kind} />)}
        </Box>
        <Box icon={<Gavel size={16} />} title="Procurement approvals" count={awards.length + procAsks.length} link={{ to: "/approvals", label: "Approvals" }}>
          {awards.map((a) => (
            <li key={a.id} className="flex items-center gap-2 rounded-lg px-3 py-2 hover:bg-black/[0.03]">
              <a href={`/approvals/${a.id}`} className="min-w-0 flex-1"><span className="block truncate text-sm font-medium">{a.title}</span><span className="muted block truncate text-xs">{a.ref} · award{a.questionState === "awaiting_answer" ? " · waiting for an answer" : a.questionState === "answered" ? " · answered" : ""}</span></a>
              {a.amount && <span className="num shrink-0 text-xs font-semibold">{eur(a.amount, a.currency)}</span>}
            </li>
          ))}
          {procAsks.map((r) => <AskLine key={r.id} r={r} />)}
        </Box>
        <Box icon={<Inbox size={16} />} title="Mythos Tasks" count={mythos.length}>{mythos.map((r) => <AskLine key={r.id} r={r} />)}</Box>
        <DelegatedBox d={d?.delegated} fusion={fusion} />
        <Box icon={<ExternalLink size={16} />} title="Ops Inbox" count={ops.length}>{ops.map((r) => <AskLine key={r.id} r={r} />)}</Box>
      </div>
      {nOther > 0 && (
        <Box icon={<Gavel size={16} />} title="Other decisions" count={nOther}>
          {renderDecide && otherDecide.length > 0 && <li className="list-none">{renderDecide(otherDecide)}</li>}
          {otherAsks.map((r) => <AskLine key={r.id} r={r} />)}
        </Box>
      )}
    </section>
  );
}

const dayLabel = (iso: string) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
const overdue = (iso: string | null) => !!iso && Date.parse(iso) < new Date().setHours(0, 0, 0, 0);

/**
 * "Delegated to me" (replaces the FusionTask box, Liam 27 Oct): every open task someone gave you in ONE — it stays here
 * after you plan it, until it's done — then work assigned to you in the other apps, then FusionTask's asks (folded away).
 */
function DelegatedBox({ d, fusion }: { d: CameIn["delegated"]; fusion: AskRow[] }) {
  const [asksOpen, setAsksOpen] = useState(false);
  const tasks = d?.tasks ?? [], apps = d?.apps ?? [];
  const due = (iso: string | null) => iso ? <span className={overdue(iso) ? "font-semibold text-red-600" : ""}>due {dayLabel(iso)}</span> : null;
  return (
    <Box icon={<CheckCircle2 size={16} />} title="Delegated to me" count={tasks.length + apps.length + fusion.length}>
      {tasks.map((r) => (
        <li key={r.id} className="flex items-center gap-2 rounded-lg px-3 py-2 hover:bg-black/[0.03]">
          <Link to="/work/$id" params={{ id: r.id }} className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium">{r.title}</span>
            <span className="muted block truncate text-xs">from {r.by} · {r.spaceName}{r.dueAt && <> · {due(r.dueAt)}</>}{r.plannedDate ? ` · planned ${dayLabel(r.plannedDate)}` : ""}{r.state === "waiting" ? " · waiting" : ""}</span>
          </Link>
          {!r.plannedDate && <MoveTask id={r.id} title={r.title} plan compact />}
        </li>
      ))}
      {apps.map((r) => (
        <li key={`${r.system}:${r.id}`} className="flex items-center gap-2 rounded-lg px-3 py-2 hover:bg-black/[0.03]">
          <a href={r.url ?? "#"} target="_blank" rel="noreferrer" className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium">{r.title}</span>
            <span className="muted block truncate text-xs">{r.app}{r.ref ? ` · ${r.ref}` : ""} · {r.status.replace(/_/g, " ")}{r.dueAt && <> · {due(r.dueAt)}</>}</span>
          </a>
          <ExternalLink size={13} className="muted shrink-0" />
        </li>
      ))}
      {fusion.length > 0 && (
        <li className="list-none">
          <button type="button" className="muted flex w-full items-center gap-1.5 px-3 py-1.5 text-xs hover:text-[var(--text)]" onClick={() => setAsksOpen((x) => !x)} aria-expanded={asksOpen}>
            {asksOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />} {fusion.length} ask{fusion.length === 1 ? "" : "s"} from FusionTask
          </button>
          {asksOpen && <ul>{fusion.map((r) => <AskLine key={r.id} r={r} />)}</ul>}
        </li>
      )}
    </Box>
  );
}

/** One quiet line at the bottom of Today: comments between others, team notes, other apps. Open it when tracing something. */
export function OtherUpdates() {
  const qc = useQueryClient();
  const q = useCameIn();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const u = q.data?.updates;
  const n = u ? u.comments.length + u.notes.length + u.apps.length : 0;
  if (!u || !n) return null;
  const parts = [u.comments.length && `${u.comments.length} comment${u.comments.length === 1 ? "" : "s"}`, u.notes.length && `${u.notes.length} team note${u.notes.length === 1 ? "" : "s"}`, u.apps.length && `${u.apps.length} in other apps`].filter(Boolean).join(" · ");
  const markSeen = async () => {
    setBusy(true);
    try { await post("/api/came-in/seen", { upTo: q.data!.asOf }); await qc.invalidateQueries({ queryKey: ["came-in"] }); } finally { setBusy(false); }
  };
  const line = (title: string, sub: string, at: string) => (
    <span className="flex items-baseline gap-3 rounded-lg px-3 py-1.5 hover:bg-black/[0.03]">
      <span className="min-w-0 flex-1"><span className="block truncate text-sm">{title}</span><span className="muted block truncate text-xs">{sub}</span></span>
      <span className="muted shrink-0 text-xs">{ago(at)}</span>
    </span>
  );
  return (
    <div className="rounded-xl border border-[var(--line)] px-4 py-2.5 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className="muted flex items-center gap-1.5 hover:text-[var(--text)]" onClick={() => setOpen((x) => !x)} aria-expanded={open}>
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />} {n} other update{n === 1 ? "" : "s"} {sinceLabel(q.data!.since)}: {parts}
        </button>
        <span className="flex-1" />
        <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void markSeen()}><Check size={13} /> Mark seen</button>
      </div>
      {open && (
        <ul className="mt-2 space-y-0.5">
          {u.comments.map((r) => <li key={`c-${r.id}`}><Link to="/work/$id" params={{ id: r.id }} className="flex items-start gap-2"><MessageSquare size={13} className="muted mt-2.5 shrink-0" />{line(r.title, `${r.by ?? "Someone"}${r.n && r.n > 1 ? ` +${r.n - 1}` : ""}: ${r.snippet ?? ""}`, r.at)}</Link></li>)}
          {u.notes.map((r) => <li key={`n-${r.id}`}><a href={`/notes?note=${r.id}`} className="flex items-start gap-2"><FileText size={13} className="muted mt-2.5 shrink-0" />{line(r.title || "Untitled", `${r.isNew ? "new" : "updated"} by ${r.by} · ${r.team}`, r.at)}</a></li>)}
          {u.apps.map((r) => <li key={`a-${r.system}-${r.id}`}><a href={r.url ?? "#"} target="_blank" rel="noreferrer" className="flex items-start gap-2"><ExternalLink size={13} className="muted mt-2.5 shrink-0" />{line(r.title, r.system ?? "", r.at)}</a></li>)}
        </ul>
      )}
    </div>
  );
}
